"use client"

import { Star, ChevronLeft, ChevronRight, Quote, Award, Sparkles, Trophy } from "lucide-react"
import { motion } from "framer-motion"
import { cn } from "@/lib/utils"
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel"
import { Card } from "@/components/ui/card"
import Autoplay from "embla-carousel-autoplay"
import * as React from "react"
import { AnimatePresence } from "framer-motion"

const SPOTLIGHT_RANKERS = [
  {
    image: "/images/og1.jpeg",
    name: "Top Achiever",
    rank: "Official Selection",
    category: "Final Selection",
  },
  {
    image: "/images/og2.jpeg",
    name: "Top Achiever",
    rank: "Official Selection",
    category: "Final Selection",
  }
]

const RANKERS = [
  { image: "/images/rank 39.jpeg", name: "K. Sai Nath Reddy", rank: "State Rank 39", highlighted: true },
  { image: "/images/WhatsApp Image 2026-04-19 at 2.06.23 PM.jpeg", name: "Anish K. Reddy", rank: "Official Ranker" },
  { image: "/images/WhatsApp Image 2026-04-19 at 2.06.247 PM.jpeg", name: "Sirisha Murali", rank: "Official Ranker" },
  { image: "/images/WhatsApp Image 2026-04-19 at 2.06.24 PM.jpeg", name: "Priya Sharma", rank: "Official Ranker" },
  { image: "/images/WhatsApp Image 2026-04-19 at 2.06.25 PM.jpeg", name: "Rajesh Kumar", rank: "Official Ranker" },
  { image: "/images/WhatsApp Image 2026-04-19 at 2.06.26 PM.jpeg", name: "P. Vinay Rao", rank: "Official Ranker" },
  { image: "/images/WhatsApp Image 2026-04-19 at 2.06.28 PM.jpeg", name: "Ananya Patel", rank: "Official Ranker" },
  { image: "/images/WhatsApp Image 2026-04-19 at 2.06.29 PM.jpeg", name: "D. Sneha", rank: "Official Ranker" },
]



export default function TestimonialsSection() {
  const [currentIndex, setCurrentIndex] = React.useState(0)
  const [showAllRankers, setShowAllRankers] = React.useState(false)

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % SPOTLIGHT_RANKERS.length)
    }, 4000)
    return () => clearInterval(timer)
  }, [])

  const activeSpotlight = SPOTLIGHT_RANKERS[currentIndex]
  const others = RANKERS

  return (
    <section id="testimonials" className="bg-[#fcfaf7] py-8 sm:py-14 overflow-hidden relative border-y border-slate-100">
      {/* Decorative Branding Watermark */}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-1/4 opacity-[0.012] pointer-events-none select-none">
        <Trophy size={400} strokeWidth={0.5} />
      </div>

      <div className="max-w-[1240px] mx-auto px-6 relative z-10">
        
        {/* Editorial Header */}
        <div className="mb-6 sm:mb-8 border-l-2 border-[#b48c36] pl-4 sm:pl-5 max-w-2xl">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-[8px] sm:text-[9px] font-bold uppercase tracking-[0.4em] text-[#b48c36] mb-1 sm:mb-2">
              Success Gallery
            </p>
            <h2 className="font-serif text-xl sm:text-3xl lg:text-4xl text-slate-900 leading-tight">
              Celebrating <span className=" font-light text-slate-500">Student Success</span> <br className="hidden sm:block" />
              at Rohith's Academy.
            </h2>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-start">
          
          {/* Spotlight Hero (Rank 39) */}
          <div className="lg:col-span-4 flex justify-center lg:block">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative w-full max-w-[260px] lg:max-w-none"
            >
              <div className="absolute -inset-1 bg-gradient-to-tr from-[#b48c36]/15 to-transparent rounded-[1.2rem] blur-xl opacity-30" />
              <Card className="relative overflow-hidden border-none rounded-[0.8rem] bg-white shadow-xl shadow-slate-200/40">
                <div className="relative aspect-[4/5] bg-slate-900">
                  <AnimatePresence mode="wait">
                    <motion.img 
                      key={activeSpotlight.image}
                      initial={{ opacity: 0, scale: 1.05 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.8, ease: "easeInOut" }}
                      src={activeSpotlight.image} 
                      alt="Featured Ranker"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </AnimatePresence>
                  
                  <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-transparent z-10" />
                  
                  <div className="absolute bottom-4 left-4 right-4 z-20">
                    <div className="flex items-center gap-1.5 mb-2">
                      <div className="px-2 py-0.5 bg-[#b48c36] rounded-full">
                        <p className="text-[7px] font-bold uppercase tracking-widest text-white flex items-center gap-1">
                          <Sparkles size={7} /> Spotlight
                        </p>
                      </div>
                      
                      {/* Pagination Dots */}
                      <div className="flex gap-1 ml-auto">
                        {SPOTLIGHT_RANKERS.map((_, i) => (
                          <div 
                            key={i} 
                            className={`h-1 rounded-full transition-all duration-500 ${i === currentIndex ? 'w-3 bg-[#b48c36]' : 'w-1 bg-white/30'}`}
                          />
                        ))}
                      </div>
                    </div>
                    
                    <div className="h-[60px] flex flex-col justify-end">
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={activeSpotlight.name}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.5 }}
                        >
                          <h3 className="text-xl font-serif font-bold text-white mb-0.5 leading-none">{activeSpotlight.rank}</h3>
                          <p className="text-white/90 text-sm font-medium mb-1">{activeSpotlight.name}</p>
                          <p className="text-[#b48c36] text-[9px] font-bold uppercase tracking-widest">{activeSpotlight.category}</p>
                        </motion.div>
                      </AnimatePresence>
                    </div>
                  </div>

                  <div className="absolute top-2.5 right-2.5 bg-slate-950/20 backdrop-blur-md border border-white/10 p-1.5 rounded-full z-20">
                    <Award className="text-[#b48c36] size-4" strokeWidth={1.5} />
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>

          {/* Secondary Carousel */}
          <div className="lg:col-span-8">
            <div className="flex flex-col h-full justify-center">
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                }}
                plugins={[
                  Autoplay({
                    delay: 2000,
                    stopOnInteraction: false,
                  }),
                ]}
                className="w-full"
              >
                <div className="flex items-center justify-between mb-3.5">
                  <h4 className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-400">Class of 2024 Records</h4>
                  <div className="flex gap-1.5">
                    <CarouselPrevious className="static translate-y-0 h-7 w-7 border-slate-200 hover:bg-[#b48c36] hover:text-white transition-all" />
                    <CarouselNext className="static translate-y-0 h-7 w-7 border-slate-200 hover:bg-[#b48c36] hover:text-white transition-all" />
                  </div>
                </div>

                <CarouselContent className="-ml-3 sm:-ml-4">
                  {others.map((ranker, idx) => (
                    <CarouselItem key={idx} className="pl-3 sm:pl-4 basis-[75%] sm:basis-1/2 md:basis-1/3">
                      <motion.div
                        whileHover={{ y: -5 }}
                        initial={{ opacity: 0, scale: 0.98 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: (idx % 7) * 0.05, duration: 0.4 }}
                      >
                        <Card className={cn(
                          "group relative overflow-hidden rounded-[1.2rem] bg-white cursor-pointer transition-shadow duration-500",
                          ranker.highlighted ? "border-2 border-[#b48c36] shadow-xl shadow-[#b48c36]/20 ring-4 ring-[#b48c36]/10" : "border-none shadow-lg hover:shadow-2xl hover:shadow-[#b48c36]/10"
                        )}>
                          {ranker.highlighted && (
                            <div className="absolute top-3 right-3 z-20">
                              <div className="bg-[#b48c36] text-white p-1.5 rounded-full shadow-lg">
                                <Star size={12} fill="currentColor" />
                              </div>
                            </div>
                          )}
                          <div className="aspect-[3/4] relative">
                            <img 
                              src={ranker.image} 
                              alt={ranker.name}
                              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                            />
                            
                            {/* Smooth bottom gradient for text contrast */}
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/30 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                            
                            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 z-10 transform transition-transform duration-500 group-hover:-translate-y-1">
                              <p className="text-[12px] sm:text-[14px] font-serif font-bold text-white mb-1.5 drop-shadow-md">{ranker.name}</p>
                              <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 bg-[#b48c36] rounded-full shadow-[0_0_5px_rgba(180,140,54,0.6)]" />
                                <p className="text-[7px] sm:text-[8px] font-bold uppercase tracking-[0.15em] text-[#b48c36] drop-shadow-sm">{ranker.rank}</p>
                              </div>
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
              
              {/* Verified Institutional Metrics Grid */}
              <div className="mt-10 pt-6 border-t border-slate-100">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                  {[
                    { stat: "45+", label: "Selections", title: "Group 1 Dominance", desc: "Recent Cycles" },
                    { stat: "Rank 1", label: "Every Cycle", title: "Top Ranks", desc: "2021 & 2022" },
                    { stat: "15+", label: "Mentors", title: "Expert Faculty", desc: "Ex-Bureaucrats" },
                    { stat: "78%", label: "Placed", title: "Success Rate", desc: "Interview-Guided" }
                  ].map((item, i) => (
                    <div key={i} className="flex flex-col">
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className="size-1 bg-[#b48c36] rounded-full" />
                        <span className="text-[7px] font-bold uppercase tracking-wider text-slate-300">Verified</span>
                      </div>
                      <p className="text-sm sm:text-lg font-serif font-bold text-slate-900 leading-none">{item.stat}</p>
                      <p className="text-[8px] font-bold text-[#b48c36] uppercase tracking-widest mt-0.5">{item.title}</p>
                      <p className="text-[7px] text-slate-400  mt-1 leading-tight">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* View All Button & Expandable Grid */}
        <div className="w-full mt-10 flex flex-col items-center">
          <button 
            onClick={() => setShowAllRankers(!showAllRankers)}
            className="flex items-center gap-2 px-8 py-3.5 rounded-full bg-white border border-[#b48c36]/20 text-slate-900 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-[#b48c36] hover:text-white hover:border-[#b48c36] transition-all duration-300 shadow-md hover:shadow-xl hover:shadow-[#b48c36]/10"
          >
            {showAllRankers ? 'Hide All Rank Holders' : 'View All Rank Holders'}
            <ChevronRight 
              size={14}
              className={cn("transition-transform duration-300", showAllRankers ? "rotate-90" : "rotate-0")} 
            />
          </button>

          <AnimatePresence>
            {showAllRankers && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="w-full overflow-hidden"
              >
                <div className="mt-8 pt-10 border-t border-slate-200">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-8 sm:gap-10 justify-items-center px-4">
                    {[...SPOTLIGHT_RANKERS, ...RANKERS].map((r, i) => (
                      <motion.div 
                        key={i}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.04, duration: 0.4, type: "spring", bounce: 0.4 }}
                        className="group flex flex-col items-center"
                      >
                        <div className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-[4px] border-slate-100 group-hover:border-[#b48c36] scale-100 group-hover:scale-110 transition-all duration-300 relative shadow-md group-hover:shadow-[0_0_20px_rgba(180,140,54,0.4)]">
                          <img src={r.image} alt="Rank Holder" className="w-full h-full object-cover" />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

      </div>
    </section>
  )
}

